import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Upload, X, Image as ImageIcon, Loader2, Maximize2, ChevronLeft, ChevronRight, GripVertical, Star, Settings, Trash2, Check, AlertCircle } from 'lucide-react';
import { useTranslation } from '../context/LanguageContext';
import { useNotification } from '../context/NotificationContext';

const apiFetch = async (url, options = {}) => {
  const token = localStorage.getItem('token');
  const headers = { ...options.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(url, { ...options, headers });
  if (res.status === 401) {
    localStorage.removeItem('token');
    window.location.href = '/login';
    throw new Error('Session expired');
  }
  return res.json();
};

/**
 * M2 Media Gallery 组件
 *
 * 管理 channel_product 的图片（本地 channel_product_media 表 → M2 API 同步）。
 *
 * 支持两种模式：
 *   编辑模式 (entityId 存在)：从 API 加载、上传到 channel/media/upload
 *   新建模式 (entityId 为 null)：通过 /api/media/upload 上传（无需 ID），URL + 元数据存本地 state，
 *     通过 onImagesChange 回调通知父组件。提交时随产品数据发送后端。
 *
 * Props:
 *   entityId       - entity ID（编辑模式必需）
 *   viewOnly       - 只读模式
 *   onImagesChange - (images[]) => void，新建模式时图片变化通知父组件
 */
const IMAGE_ROLES = [
  { key: 'image', label: 'base' },
  { key: 'small_image', label: 'small' },
  { key: 'thumbnail', label: 'thumbnail' },
  { key: 'swatch_image', label: 'swatch' },
];

const ROLE_COLORS = {
  image: { bg: 'bg-blue-100 dark:bg-blue-900/40', text: 'text-blue-700 dark:text-blue-300', border: 'border-blue-500' },
  small_image: { bg: 'bg-purple-100 dark:bg-purple-900/40', text: 'text-purple-700 dark:text-purple-300', border: 'border-purple-500' },
  thumbnail: { bg: 'bg-green-100 dark:bg-green-900/40', text: 'text-green-700 dark:text-green-300', border: 'border-green-500' },
  swatch_image: { bg: 'bg-amber-100 dark:bg-amber-900/40', text: 'text-amber-700 dark:text-amber-300', border: 'border-amber-500' },
};

// ── 局部唯一 ID 计数器（新建模式）──
let localIdCounter = 0;
const nextLocalId = () => `_local_${++localIdCounter}`;

const M2MediaGallery = ({ entityId, viewOnly = false, onImagesChange }) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const inputRef = useRef(null);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [reorderPending, setReorderPending] = useState(false);

  // ── 新建模式下的临时图片 ──
  const [pendingImages, setPendingImages] = useState([]);

  // ── 当前展示的数据源 ──
  const displayItems = useMemo(() => entityId ? items : pendingImages, [entityId, items, pendingImages]);

  // ── 通知父组件 ──
  const notifyParent = useCallback((imgs) => {
    if (!entityId && onImagesChange) onImagesChange(imgs);
  }, [entityId, onImagesChange]);

  // ── 编辑模式：从 API 加载 ──
  const loadMedia = useCallback(async () => {
    if (!entityId) { setLoading(false); return; }
    try {
      const res = await apiFetch(`/api/channel/media?entity_id=${entityId}`);
      if (res.status === 'success') setItems(res.data || []);
    } catch (e) {
      console.error("Load media failed:", e);
    } finally {
      setLoading(false);
    }
  }, [entityId]);

  useEffect(() => { loadMedia(); }, [loadMedia]);

  // ── 上传文件 ──
  // 编辑模式 → /api/channel/media/upload （需 entityId）
  // 新建模式 → /api/media/upload （不需 ID，和 ERP 一样）
  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    let uploaded = 0;

    try {
      if (entityId) {
        // ═══ 编辑模式 ═══
        for (const file of files) {
          const fd = new FormData();
          fd.append('file', file);
          fd.append('entity_id', String(entityId));
          const res = await apiFetch('/api/channel/media/upload', {
            method: 'POST', body: fd, headers: {},
          });
          if (res.status === 'success') uploaded++;
          else notify(t('common.error') + ': ' + (res.message || t('common.unknown_error')), 'error');
        }
        if (uploaded > 0) { notify(t('m2_media.uploaded', { count: uploaded }), 'success'); await loadMedia(); }
      } else {
        // ═══ 新建模式 — 和 ERP ImageUpload 一样走 /api/media/upload ═══
        for (const file of files) {
          const fd = new FormData();
          fd.append('file', file);
          fd.append('entity', 'product');
          const res = await apiFetch('/api/media/upload', {
            method: 'POST', body: fd, headers: {},
          });
          if (res.status === 'success' && res.url) {
            const newImg = {
              _localId: nextLocalId(),
              file_path: res.url,
              url: res.url,
              types: [],
              label: '',
              position: pendingImages.length + uploaded,
            };
            setPendingImages(prev => {
              const updated = [...prev, newImg];
              notifyParent(updated);
              return updated;
            });
            uploaded++;
          } else {
            notify(t('common.error') + ': ' + (res.message || t('common.unknown_error')), 'error');
          }
        }
        if (uploaded > 0) notify(t('m2_media.uploaded', { count: uploaded }), 'success');
      }
    } catch (err) {
      notify(t('common.error') + ': ' + err.message, 'error');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  // ── 更新图片（编辑模式 → API）──
  const handleUpdate = useCallback(async (mediaId, data) => {
    try {
      const res = await apiFetch('/api/channel/media/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ media_id: mediaId, entity_id: entityId, ...data }),
      });
      if (res.status === 'success') { await loadMedia(); notify(t('common.saved'), 'success'); }
      else notify(res.message || t('common.error'), 'error');
    } catch (err) { notify(err.message, 'error'); }
  }, [entityId, loadMedia, notify, t]);

  // ── 更新图片（新建模式 → 纯本地）──
  const updatePendingImage = useCallback((localId, data) => {
    setPendingImages(prev => {
      const updated = prev.map(img => img._localId === localId ? { ...img, ...data } : img);
      notifyParent(updated);
      return updated;
    });
  }, [notifyParent]);

  // ── 删除（编辑模式）──
  const handleDelete = useCallback(async (mediaId) => {
    if (!window.confirm(t('m2_media.confirm_delete'))) return;
    try {
      const res = await apiFetch('/api/channel/media/delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ media_id: mediaId }),
      });
      if (res.status === 'success') { await loadMedia(); notify(t('common.deleted'), 'success'); }
      else notify(res.message || t('common.error'), 'error');
    } catch (err) { notify(err.message, 'error'); }
  }, [loadMedia, notify, t]);

  // ── 删除（新建模式）──
  const deletePendingImage = useCallback((localId) => {
    if (!window.confirm(t('m2_media.confirm_delete'))) return;
    setPendingImages(prev => {
      const updated = prev.filter(img => img._localId !== localId);
      notifyParent(updated);
      return updated;
    });
  }, [notifyParent, t]);

  // ── 排序持久化（编辑模式）──
  const persistReorder = useCallback(async (newItems) => {
    setReorderPending(true);
    try {
      const mediaIds = newItems.map(item => item.media_id);
      const res = await apiFetch('/api/channel/media/reorder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entity_id: entityId, media_ids: mediaIds }),
      });
      if (res.status !== 'success') notify(t('common.error'), 'error');
    } catch (err) { notify(err.message, 'error'); }
    finally { setReorderPending(false); }
  }, [entityId, notify, t]);

  // ── 拖拽结束 ──
  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === dropIndex) {
      setDragIndex(null); setDragOverIndex(null); return;
    }
    const current = entityId ? [...items] : [...pendingImages];
    const [moved] = current.splice(dragIndex, 1);
    current.splice(dropIndex, 0, moved);
    if (entityId) { setItems(current); persistReorder(current); }
    else { setPendingImages(current); notifyParent(current); }
    setDragIndex(null); setDragOverIndex(null);
  };

  // ── 角色切换 ──
  const handleToggleRole = (item, roleKey) => {
    const currentTypes = item.types || [];
    const newTypes = currentTypes.includes(roleKey)
      ? currentTypes.filter(r => r !== roleKey)
      : [...currentTypes, roleKey];
    if (entityId) handleUpdate(item.media_id, { types: newTypes });
    else updatePendingImage(item._localId, { types: newTypes });
  };

  // ── 编辑模态框保存标签和角色 ──
  const saveLabel = () => {
    if (!editModal) return;
    const payload = { label: editModal.label };
    if (editModal.media.types) {
      payload.types = editModal.media.types;
    }
    if (entityId) handleUpdate(editModal.media.media_id, payload);
    else updatePendingImage(editModal.media._localId, payload);
    setEditModal(null);
  };

  // ── Loader ──
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  // ── 上传按钮（通用）──
  const renderUploadButton = () => (
    <label className={`flex flex-col items-center justify-center w-full min-h-[100px] cursor-pointer
      border-2 border-dashed rounded-xl transition-colors
      ${uploading
        ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20'
        : 'border-slate-300 dark:border-slate-600 hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50/50 dark:hover:bg-blue-900/10'
      }`}
    >
      {uploading ? (
        <div className="flex flex-col items-center gap-2 py-4">
          <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
          <span className="text-xs text-blue-500 font-medium">{t('common.uploading')}</span>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-1.5 py-4">
          <Upload className="w-6 h-6 text-slate-400" />
          <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">{t('m2_media.drag_drop_image')}</span>
          <span className="text-[10px] text-slate-400 dark:text-slate-500">JPEG, PNG, WebP</span>
        </div>
      )}
      <input ref={inputRef} type="file" accept="image/*" multiple className="hidden"
        onChange={handleFileChange} disabled={uploading || viewOnly} />
    </label>
  );

  // ── 图片缩略图 ──
  const renderThumbnail = (item, index) => (
    <div
      key={item.media_id || item._localId}
      draggable={!viewOnly}
      onDragStart={() => !viewOnly && handleDragStart(index)}
      onDragOver={(e) => !viewOnly && handleDragOver(e, index)}
      onDragLeave={!viewOnly ? handleDragLeave : undefined}
      onDrop={(e) => !viewOnly && handleDrop(e, index)}
      onDragEnd={!viewOnly ? handleDragEnd : undefined}
      className={`relative group rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-800 aspect-square
        ${dragIndex === index ? 'opacity-50 scale-95' : ''}
        ${dragOverIndex === index ? 'ring-2 ring-blue-400' : ''}
        ${!viewOnly ? 'cursor-grab active:cursor-grabbing' : ''}
        transition-all duration-150`}
    >
      <img src={item.media_url || item.file_path || item.url} alt={item.label || ''}
        className="w-full h-full object-cover"
        onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.classList.add('bg-slate-200', 'dark:bg-slate-700'); }}
      />
      {/* Role badges */}
      {(item.types || []).length > 0 && (
        <div className="absolute top-1 left-1 flex flex-col gap-0.5">
          {item.types.map(roleKey => (
            <span key={roleKey}
              className={`px-1 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${ROLE_COLORS[roleKey]?.text || 'text-white'} drop-shadow-md`}
            >
              {roleKey === 'image' ? 'B' : roleKey === 'small_image' ? 'S' : roleKey === 'thumbnail' ? 'T' : 'SW'}
            </span>
          ))}
        </div>
      )}
      {/* Drag handle */}
      {!viewOnly && (
        <div className="absolute top-1 right-1 p-1 bg-black/30 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-grab">
          <GripVertical size={12} className="text-white" />
        </div>
      )}
      {/* Hover overlay */}
      {!viewOnly && (
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5">
          <button type="button"
            onClick={(e) => { e.stopPropagation(); setPreviewIndex(index); }}
            className="p-1.5 bg-white/90 text-slate-700 rounded-full hover:bg-white transition-colors shadow-lg hover:scale-110 transform"
            title={t('common.view')}
          >
            <Maximize2 size={13} />
          </button>
          <button type="button"
            onClick={(e) => {
              e.stopPropagation();
              setEditModal({ index, media: { ...item }, label: item.label || '' });
            }}
            className="p-1.5 bg-slate-600/90 text-white rounded-full hover:bg-slate-700 transition-colors shadow-lg hover:scale-110 transform"
            title={t('common.settings')}
          >
            <Settings size={13} />
          </button>
          <button type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (entityId) handleDelete(item.media_id);
              else deletePendingImage(item._localId);
            }}
            className="p-1.5 bg-red-500/90 text-white rounded-full hover:bg-red-600 transition-colors shadow-lg hover:scale-110 transform"
            title={t('common.delete')}
          >
            <Trash2 size={13} />
          </button>
        </div>
      )}
      {/* Position badge */}
      <div className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-black/50 text-white text-[10px] rounded font-mono opacity-0 group-hover:opacity-100 transition-opacity">
        #{index + 1}
      </div>
    </div>
  );

  // ── 拖拽 handlers ──
  const handleDragStart = (index) => setDragIndex(index);
  const handleDragOver = (e, index) => { e.preventDefault(); e.stopPropagation(); setDragOverIndex(index); };
  const handleDragLeave = () => setDragOverIndex(null);
  const handleDragEnd = () => { setDragIndex(null); setDragOverIndex(null); };

  // ── 主渲染 ──
  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex justify-between items-center">
        <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
          {t('m2_media.media_gallery')}
        </label>
        <div className="flex items-center gap-2">
          {reorderPending && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
          <span className="text-xs text-slate-400 dark:text-slate-500">
            {displayItems.length > 0
              ? t('m2_media.image_count', { count: displayItems.length })
              : (entityId || !viewOnly ? t('components.image_upload.optional') : '')
            }
          </span>
        </div>
      </div>

      {/* Lightbox Modal */}
      {previewIndex !== null && displayItems[previewIndex] && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center"
          onClick={() => setPreviewIndex(null)}
        >
          <div className="relative max-w-5xl max-h-[92vh] mx-4 flex flex-col items-center"
            onClick={e => e.stopPropagation()}
          >
            <div className="absolute -top-8 left-1/2 -translate-x-1/2 text-white/70 text-sm font-medium">
              {previewIndex + 1} / {displayItems.length}
            </div>
            <button type="button" onClick={() => setPreviewIndex(null)}
              className="absolute -top-10 right-0 p-1.5 text-white/60 hover:text-white transition-colors"
            >
              <X size={20} />
            </button>
            <img src={displayItems[previewIndex].media_url || displayItems[previewIndex].file_path || displayItems[previewIndex].url}
              alt={displayItems[previewIndex].label || ''}
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
              style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}
            />
            {displayItems.length > 1 && (
              <>
                <button type="button"
                  onClick={() => setPreviewIndex(prev => prev > 0 ? prev - 1 : displayItems.length - 1)}
                  className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-white/10 hover:bg-white/20 text-white rounded-full backdrop-blur-sm"
                ><ChevronLeft size={22} /></button>
                <button type="button"
                  onClick={() => setPreviewIndex(prev => prev < displayItems.length - 1 ? prev + 1 : 0)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-white/10 hover:bg-white/20 text-white rounded-full backdrop-blur-sm"
                ><ChevronRight size={22} /></button>
              </>
            )}
            {/* Role badges */}
            <div className="mt-2 flex gap-1.5 flex-wrap justify-center">
              {(displayItems[previewIndex].types || []).map(roleKey => (
                <span key={roleKey}
                  className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${ROLE_COLORS[roleKey]?.bg || 'bg-gray-100'} ${ROLE_COLORS[roleKey]?.text || 'text-gray-600'}`}
                >{roleKey}</span>
              ))}
              {displayItems[previewIndex].label && (
                <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                  {displayItems[previewIndex].label}
                </span>
              )}
            </div>
            {/* Thumbnail strip */}
            <div className="mt-3 flex gap-1.5 max-w-full overflow-x-auto pb-1">
              {displayItems.map((item, i) => (
                <button type="button" key={item.media_id || item._localId}
                  onClick={() => setPreviewIndex(i)}
                  className={`shrink-0 w-12 h-12 rounded-lg overflow-hidden border-2 transition-all ${
                    i === previewIndex ? 'border-blue-500 opacity-100' : 'border-transparent opacity-50 hover:opacity-80'
                  }`}
                >
                  <img src={item.media_url || item.file_path || item.url} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => setEditModal(null)}
        >
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl max-w-md w-full p-5"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200">{t('m2_media.edit_image')}</h3>
              <button type="button" onClick={() => setEditModal(null)}
                className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
              ><X size={18} /></button>
            </div>
            <img src={editModal.media.media_url || editModal.media.file_path || editModal.media.url}
              alt="" className="w-full h-40 object-cover rounded-lg mb-4" />
            {/* Alt Text */}
            <div className="mb-4">
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">{t('m2_media.alt_text')}</label>
              <input type="text" value={editModal.label}
                onChange={e => setEditModal({ ...editModal, label: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg
                  bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200
                  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                placeholder={t('m2_media.alt_text_placeholder')}
              />
            </div>
            {/* Image Roles */}
            <div className="mb-4">
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">{t('m2_media.image_roles')}</label>
              <div className="flex flex-wrap gap-2">
                {IMAGE_ROLES.map(role => {
                  const isActive = (editModal.media.types || []).includes(role.key);
                  return (
                    <button type="button" key={role.key}
                      onClick={() => {
                        const current = editModal.media.types || [];
                        const newTypes = current.includes(role.key)
                          ? current.filter(r => r !== role.key)
                          : [...current, role.key];
                        setEditModal({ ...editModal, media: { ...editModal.media, types: newTypes } });
                      }}
                      className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-all ${
                        isActive
                          ? `${ROLE_COLORS[role.key]?.bg || 'bg-gray-100'} ${ROLE_COLORS[role.key]?.text || 'text-gray-600'} ${ROLE_COLORS[role.key]?.border || 'border-gray-400'} shadow-sm`
                          : 'bg-transparent border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-400 hover:border-slate-400'
                      }`}
                    >{role.label}</button>
                  );
                })}
              </div>
              <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-1.5">{t('m2_media.role_hint')}</p>
            </div>
            {/* Save */}
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-200 dark:border-slate-700">
              <button type="button" onClick={() => setEditModal(null)}
                className="px-4 py-2 text-xs font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >{t('common.cancel')}</button>
              <button type="button" onClick={saveLabel}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              ><Check size={14} />{t('common.save')}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Image Grid ── */}
      {displayItems.length > 0 && (
        <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2">
          {displayItems.map((item, index) => renderThumbnail(item, index))}
        </div>
      )}

      {/* ── Upload zone ── */}
      {displayItems.length === 0 && !viewOnly && renderUploadButton()}
      {displayItems.length > 0 && !viewOnly && <div className="mt-2">{renderUploadButton()}</div>}

      {/* ── Read-only empty ── */}
      {displayItems.length === 0 && viewOnly && (
        <div className="text-center py-6 bg-slate-50 dark:bg-slate-800/30 rounded-xl border border-dashed border-slate-200 dark:border-slate-700">
          <ImageIcon className="w-8 h-8 mx-auto mb-2 text-slate-300 dark:text-slate-600" />
          <p className="text-xs text-slate-400 dark:text-slate-500">{t('m2_media.no_images')}</p>
        </div>
      )}
    </div>
  );
};

export default M2MediaGallery;
